export const APP_TEMPLATE = `
  <header class="site-header">
    <a class="brand" href="./" aria-label="Collaborative State Demos home">
      <span class="brand__mark" aria-hidden="true">CS</span>
      <span>
        <strong>Collaborative State Demos</strong>
        <small>Public alpha · Milestone 1</small>
      </span>
    </a>
    <nav class="site-nav module-nav" aria-label="Demo modules">
      <a href="./" aria-current="page">JSON Merge</a>
      <a href="?demo=semantic-ast">Validators + AST</a>
      <a href="?demo=tct-delivery">TCT Delivery</a>
      <a href="?demo=guided-story">Guided Story</a>
      <a href="#evidence">Evidence</a>
    </nav>
  </header>

  <main id="top">
    <section class="hero" aria-labelledby="hero-title">
      <div class="hero__copy">
        <p class="eyebrow">Executable explanation · Draft revision 00</p>
        <h1 id="hero-title">Three histories in.<br /><em>One honest outcome.</em></h1>
        <p class="hero__lede">
          Explore deterministic three-way merge for restricted JSON values.
          The browser runs the pinned libdualnative WASM reference engine and
          shows complete merges, ordered conflicts, and fail-closed boundaries.
        </p>
        <div class="hero__actions">
          <a class="button button--primary" href="#playground">Open the playground</a>
          <a
            class="button button--quiet"
            href="https://datatracker.ietf.org/doc/draft-jurkovikj-json-three-way-merge/"
            target="_blank"
            rel="noreferrer"
          >Read the Internet-Draft</a>
        </div>
      </div>
      <aside class="hero__map" aria-label="The three-way merge inputs">
        <div class="history history--base"><span>01</span><strong>Base</strong><small>shared ancestor</small></div>
        <div class="history history--source"><span>02</span><strong>Source</strong><small>one derived value</small></div>
        <div class="history history--target"><span>03</span><strong>Target</strong><small>another derived value</small></div>
        <div class="history-lines" aria-hidden="true"></div>
        <div class="history history--outcome"><span>→</span><strong>Outcome</strong><small>merged or explained</small></div>
      </aside>
    </section>

    <section class="boundary-note" aria-label="Demonstration boundary">
      <strong>Educational reference execution</strong>
      <span>This is not an IETF endorsement or a conformance certificate. Inputs stay in this browser.</span>
    </section>

    <section class="playground section" id="playground" aria-labelledby="playground-title">
      <div class="section-heading">
        <div>
          <p class="eyebrow">Interactive workbench</p>
          <h2 id="playground-title">Change the histories. Inspect the rule.</h2>
        </div>
        <label class="scenario-picker">
          <span>Published scenario</span>
          <select id="scenario-select" aria-describedby="scenario-count"></select>
          <small id="scenario-count"></small>
        </label>
      </div>

      <article class="scenario-explanation" aria-live="polite">
        <div>
          <p class="eyebrow" id="scenario-category"></p>
          <h3 id="scenario-title"></h3>
          <p id="scenario-summary"></p>
        </div>
        <p class="scenario-rule">
          <strong>Rule:</strong>
          <span id="scenario-rule"></span>
          <a id="scenario-draft-link" target="_blank" rel="noreferrer">Read the governing section</a>
        </p>
      </article>

      <div class="input-grid">
        <label class="input-card input-card--base">
          <span class="input-card__heading"><strong>Base</strong><small id="base-bytes">0 bytes</small></span>
          <span class="input-card__role">Shared ancestor</span>
          <textarea id="base-input" rows="9" spellcheck="false" aria-label="Base JSON"></textarea>
        </label>
        <label class="input-card input-card--source">
          <span class="input-card__heading"><strong>Source</strong><small id="source-bytes">0 bytes</small></span>
          <span class="input-card__role">First derived value</span>
          <textarea id="source-input" rows="9" spellcheck="false" aria-label="Source JSON"></textarea>
        </label>
        <label class="input-card input-card--target">
          <span class="input-card__heading"><strong>Target</strong><small id="target-bytes">0 bytes</small></span>
          <span class="input-card__role">Second derived value</span>
          <textarea id="target-input" rows="9" spellcheck="false" aria-label="Target JSON"></textarea>
        </label>
      </div>

      <div class="run-bar">
        <div class="run-bar__controls">
          <div class="run-bar__actions">
            <button class="button button--primary" id="run-button" type="button" aria-describedby="run-hint" disabled>Edit inputs to run</button>
            <button class="button button--quiet" id="copy-case-button" type="button">Copy case</button>
            <button class="button button--quiet" id="reset-button" type="button">Reset</button>
          </div>
          <small class="run-hint" id="run-hint">Published scenarios run automatically. Edit any input to run a custom case.</small>
        </div>
        <div class="engine-state">
          <span class="engine-state__dot" aria-hidden="true"></span>
          <span id="engine-state-text">Reference engine ready on first run</span>
        </div>
      </div>

      <section class="outcome" aria-labelledby="outcome-title">
        <div class="outcome__heading">
          <div>
            <p class="eyebrow">Complete response</p>
            <h3 id="outcome-title">Outcome</h3>
          </div>
          <span class="expectation" id="expectation-state">Published fixture loaded</span>
        </div>
        <div id="result-panel" class="result-panel" aria-live="polite">
          <p class="empty-state">Run the selected case to inspect the complete engine response.</p>
        </div>
        <details class="raw-response">
          <summary>Raw compact engine response</summary>
          <pre id="raw-response">Not run yet.</pre>
        </details>
      </section>
    </section>

    <section class="concepts section" id="concepts" aria-labelledby="concepts-title">
      <div class="section-heading section-heading--compact">
        <div>
          <p class="eyebrow">Four boundaries worth seeing</p>
          <h2 id="concepts-title">Determinism without silent guesses.</h2>
        </div>
      </div>
      <div class="concept-grid">
        <article><span>01</span><h3>Missing ≠ null</h3><p>Object membership is state. A present null value is never collapsed into absence.</p></article>
        <article><span>02</span><h3>Numbers normalize</h3><p>Finite binary64 values define number equality, independent of textual spelling.</p></article>
        <article><span>03</span><h3>Conflicts stay explicit</h3><p>No last-writer-wins shortcut hides competing edits or emits a partial merge.</p></article>
        <article><span>04</span><h3>Arrays fail closed</h3><p>The base operation refuses array semantics it cannot define interoperably.</p></article>
      </div>
    </section>

    <section class="evidence section" id="evidence" aria-labelledby="evidence-title">
      <div class="evidence__copy">
        <p class="eyebrow">Pinned evidence</p>
        <h2 id="evidence-title">Know exactly what executed.</h2>
        <p>
          Every packaged scenario comes from revision 00. The engine is the
          GitHub-only libdualnative WASM alpha, loaded from this static build.
          No request is sent to an origin server when you run a merge.
        </p>
      </div>
      <dl class="evidence-list">
        <div><dt>Draft</dt><dd>draft-jurkovikj-json-three-way-merge-00</dd></div>
        <div><dt>Vectors</dt><dd id="evidence-vector-count">41 published cases</dd></div>
        <div><dt>Engine</dt><dd>libdualnative-core-v1</dd></div>
        <div><dt>WASM package</dt><dd>v0.1.0-alpha.1 · SHA-256 70c7b254…df17d</dd></div>
      </dl>
    </section>
  </main>

  <footer class="site-footer">
    <p>Collaborative State Demos · educational, browser-only reference execution</p>
    <div>
      <a href="https://github.com/DualNative/libdualnative-v1/releases/tag/v0.1.0-alpha.1" target="_blank" rel="noreferrer">Reference engine</a>
      <a href="https://datatracker.ietf.org/doc/draft-jurkovikj-json-three-way-merge/" target="_blank" rel="noreferrer">Internet-Draft</a>
    </div>
  </footer>
`;
